import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import type { Currency, SubscriptionStatus } from '@shared/contracts/enums'
import {
  SubscriptionInvoiceListSchema,
  SubscriptionListSchema,
  SubscriptionSchema,
} from './subscription.contract'
import type {
  Subscription,
  SubscriptionInvoiceList,
  SubscriptionList,
} from './subscription.contract'

export const subscriptionKeys = {
  list: (status?: SubscriptionStatus) => ['subscriptions', 'list', status ?? 'all'] as const,
  infiniteList: (status?: SubscriptionStatus, currency?: Currency) =>
    ['subscriptions', 'infinite-list', status ?? 'all', currency ?? 'all'] as const,
  detail: (subscriptionId: string) => ['subscriptions', 'detail', subscriptionId] as const,
  invoices: (subscriptionId: string) => ['subscriptions', 'invoices', subscriptionId] as const,
}

export interface SubscriptionListParams {
  readonly currency?: Currency
  readonly status?: SubscriptionStatus
  readonly limit?: number
  readonly offset?: number
}

export async function getSubscriptions(
  params: SubscriptionListParams = {},
): Promise<SubscriptionList> {
  const query = toQueryString({
    status: params.status,
    currency: params.currency,
    limit: params.limit ?? 30,
    offset: params.offset ?? 0,
  })
  return SubscriptionListSchema.parse(await apiFetch<unknown>(`/recurrence/subscriptions${query}`))
}

export function subscriptionsQuery(status?: SubscriptionStatus) {
  return queryOptions({
    queryKey: subscriptionKeys.list(status),
    queryFn: () => getSubscriptions({ status, limit: 100 }),
  })
}

export function useSubscriptionsQuery(status?: SubscriptionStatus) {
  return useQuery(subscriptionsQuery(status))
}

export function subscriptionsInfiniteQuery(status?: SubscriptionStatus, currency?: Currency) {
  return infiniteQueryOptions({
    queryKey: subscriptionKeys.infiniteList(status, currency),
    queryFn: ({ pageParam }) =>
      getSubscriptions({ status, currency, limit: 30, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: SubscriptionList) => lastPage.nextOffset,
  })
}

export function useSubscriptionsInfiniteQuery(
  status?: SubscriptionStatus,
  currency?: Currency,
  enabled = true,
) {
  return useInfiniteQuery({ ...subscriptionsInfiniteQuery(status, currency), enabled })
}

export async function getSubscription(subscriptionId: string): Promise<Subscription> {
  return SubscriptionSchema.parse(
    await apiFetch<unknown>(`/recurrence/subscriptions/${subscriptionId}`),
  )
}

export function useSubscriptionQuery(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.detail(subscriptionId ?? ''),
    queryFn: () => getSubscription(subscriptionId ?? ''),
    enabled: subscriptionId !== null,
  })
}

export async function getSubscriptionInvoices(
  subscriptionId: string,
): Promise<SubscriptionInvoiceList> {
  return SubscriptionInvoiceListSchema.parse(
    await apiFetch<unknown>(`/recurrence/subscriptions/${subscriptionId}/invoices?limit=100`),
  )
}

export function useSubscriptionInvoicesQuery(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.invoices(subscriptionId ?? ''),
    queryFn: () => getSubscriptionInvoices(subscriptionId ?? ''),
    enabled: subscriptionId !== null,
  })
}
